import { Phone, Mail, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import { SITE, STEPS } from "@/config/site";

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact DJC Global — Start With a Conversation"
        description="Tell DJC Global what you'd like to improve. We'll identify a relevant solution and connect you with the right specialist."
        path="/contact"
      />
      <PageHero
        eyebrow="Contact"
        title="Your next opportunity starts with a conversation."
        sub="Share what you'd like to improve — in plain words. We'll review it, identify the relevant destination, and connect you with the specialist who delivers it."
      />

      <section className="bg-platinum pb-20 pt-4" data-testid="contact-body">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:gap-14 lg:px-8">
          <Reveal className="lg:col-span-7">
            <ContactForm />
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-5">
            <div className="space-y-6">
              <div className="rounded-md border border-silver/60 bg-white p-6" data-testid="contact-direct-card">
                <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-cobalt">
                  Reach us directly
                </p>
                <div className="mt-4 space-y-3">
                  <a
                    href={SITE.phoneHref}
                    data-testid="contact-phone-link"
                    className="flex items-center gap-3 text-ink transition-colors hover:text-cobalt"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-cobalt/10">
                      <Phone className="h-4 w-4 text-cobalt" />
                    </span>
                    <span className="font-display text-sm font-semibold">{SITE.phone}</span>
                  </a>
                  <a
                    href={`mailto:${SITE.email}`}
                    data-testid="contact-email-link"
                    className="flex items-center gap-3 text-ink transition-colors hover:text-cobalt"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-cobalt/10">
                      <Mail className="h-4 w-4 text-cobalt" />
                    </span>
                    <span className="font-display text-sm font-semibold">{SITE.email}</span>
                  </a>
                </div>
              </div>

              <div className="rounded-md border border-silver/60 bg-white p-6" data-testid="contact-next-card">
                <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-cobalt">
                  What happens after you send
                </p>
                <ol className="mt-4 space-y-3">
                  {STEPS.map((s, i) => (
                    <li key={s.num} className="flex gap-3 text-sm leading-relaxed">
                      <span className="font-display text-xs font-bold text-cobalt">{s.num}</span>
                      <span className="text-ink/75">
                        <span className="font-semibold text-ink">{s.title}.</span>{" "}
                        {i === 3
                          ? "Pricing, eligibility, and delivery depend on the selected provider."
                          : s.desc}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-md border border-silver/60 bg-platinum p-6" data-testid="contact-privacy-card">
                <p className="text-xs leading-relaxed text-ink/60">
                  Please don't include patient information, financial account details, or sensitive
                  documents — we don't need them to help. See our{" "}
                  <Link to="/privacy" className="font-semibold text-cobalt hover:text-midnight">
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/referral-disclosure" className="font-semibold text-cobalt hover:text-midnight">
                    Referral Disclosure
                  </Link>
                  .
                </p>
              </div>

              <div className="rounded-md bg-midnight p-6 text-white" data-testid="contact-browse-card">
                <p className="font-display text-lg font-bold">Prefer to browse first?</p>
                <p className="mt-2 text-sm leading-relaxed text-white/75">
                  You never need a form to visit a provider. Explore any destination and use its
                  partner link whenever you're ready.
                </p>
                <Link
                  to="/solutions"
                  data-testid="contact-solutions-cta"
                  className="group mt-4 inline-flex items-center gap-2 rounded-md bg-white px-5 py-2.5 font-display text-sm font-semibold text-midnight transition-colors hover:bg-silver"
                >
                  Explore the Solutions
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
